/**
 * Extracts the file name and extension from a given file name with extension.
 * @param {string} fileNameWithExtension - The file name with extension (e.g., "example.txt").
 * @returns {{name: string, extension: string|null}} An object containing the file name without extension and the file extension in lowercase. If no extension is found, the extension will be null.
 */
const _getFileNameAndExtension = (fileNameWithExtension) => {
	const lastDotIndex = fileNameWithExtension.lastIndexOf('.');
	if (
		lastDotIndex === -1 ||
		lastDotIndex === 0 ||
		lastDotIndex === fileNameWithExtension.length - 1
	) {
		return { name: fileNameWithExtension, extension: null };
	}
	const name = fileNameWithExtension.substring(0, lastDotIndex);
	const extension = fileNameWithExtension
		.substring(lastDotIndex + 1)
		.toLowerCase();
	return { name, extension };
};

/**
 * Attempts to fetch a local data file by trying a list of extensions.
 * It tries the first extension, and if it fails with a 404 and a second extension exists, it tries the second one.
 * @param {string} urlNoExtension - The base URL of the file without an extension.
 * @param {string[]} extensions - An array of file extensions to try (e.g., ['json', 'xml']).
 * @returns {Promise<{response: Response, type: string|null}>} An object containing the fetch Response and the type (extension) of the successfully fetched file, or the last response if all attempts fail. Type is null if no file was successfully fetched.
 */
const _tryFetchLocalDataFile = async (urlNoExtension, extensions) => {
	// Try first extension (e.g., json)
	let response = await fetch(
		/* webpackMode: "eager" */
		`${urlNoExtension}.${extensions[0]}`,
		{ cache: 'no-store' }
	);
	if (response.ok) {
		return { response, type: extensions[0] };
	}

	// If first failed with 404 and a second extension exists (e.g., xml)
	if (response.status === 404 && extensions.length > 1) {
		console.log(
			`%cLocal .${extensions[0]} file not found, trying .${extensions[1]}`,
			'color: orange;'
		);
		response = await fetch(
			/* webpackMode: "eager" */
			`${urlNoExtension}.${extensions[1]}`,
			{ cache: 'no-store' }
		);
		if (response.ok) {
			return { response, type: extensions[1] };
		}
	}
	// Return the last response or error if both failed
	return {
		response,
		type: response.ok ? extensions[extensions.length - 1] : null,
	};
};

/**
 * Attempts to parse a Response object as JSON.
 * @param {Response} res - The Response object to parse.
 * @returns {Promise<{data: object, type: 'json'}|null>} An object containing the parsed JSON data and type 'json', or null if parsing fails.
 */
const _tryParseJSON = async (res) => {
	try {
		const data = await res.json();
		return { data, type: 'json' };
	} catch (e) {
		console.warn('Attempt to parse as JSON failed:', e.message);
		return null;
	}
};

/**
 * Attempts to parse a Response object as XML.
 * @param {Response} res - The Response object to parse.
 * @returns {Promise<{data: Document, type: 'xml'}|null>} An object containing the parsed XML Document and type 'xml', or null if parsing fails or a parser error is detected in the XML.
 */
const _tryParseXML = async (res) => {
	try {
		const xmlText = await res.text();
		const parser = new DOMParser();
		const xmlDoc = parser.parseFromString(xmlText, 'application/xml');

		if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
			console.warn('XML parsing error detected by DOMParser.');
			return null;
		}
		return { data: xmlDoc, type: 'xml' };
	} catch (e) {
		console.warn('Attempt to parse as XML failed:', e.message);
		return null;
	}
};

/**
 * Converts an XML Document object to a JavaScript object.
 * Attributes are prefixed with "@" and text content is under "#text" if mixed with elements.
 * Tries to handle simple cases of repeated elements as arrays.
 * @param {Document} xml - The XML Document object to convert.
 * @returns {object|null} The converted JavaScript object, or null if conversion fails or input is invalid.
 */
const _xmlToJsObject = (xml) => {
	if (!xml || !(xml instanceof Document)) {
		console.error('Invalid XML document provided to xmlToJsObject', xml);
		return null;
	}
	const root = xml.documentElement;

	const convertNode = (node) => {
		let obj = {};

		if (node.nodeType === 1) {
			// Element node
			if (node.attributes.length > 0) {
				obj['@attributes'] = {};
				for (let j = 0; j < node.attributes.length; j++) {
					const attribute = node.attributes.item(j);
					obj['@attributes'][attribute.nodeName] = attribute.nodeValue;
				}
			}
		} else if (node.nodeType === 3) {
			// Text node
			return node.nodeValue.trim();
		}

		if (node.hasChildNodes()) {
			let textOnlyChildren = true;
			let accumulatedText = '';
			for (let i = 0; i < node.childNodes.length; i++) {
				if (node.childNodes.item(i).nodeType !== 3) {
					textOnlyChildren = false;
					break;
				}
				accumulatedText += node.childNodes.item(i).nodeValue;
			}

			if (textOnlyChildren && accumulatedText.trim() !== '') {
				const trimmedText = accumulatedText.trim();
				if (Object.keys(obj).length === 0) return trimmedText; // Element ma tylko tekst
				obj['#text'] = trimmedText; // Element ma atrybuty i tekst
			} else {
				for (let i = 0; i < node.childNodes.length; i++) {
					const item = node.childNodes.item(i);
					const nodeName = item.nodeName;
					// Pomiń puste węzły tekstowe (np. tylko białe znaki między tagami)
					if (item.nodeType === 3 && item.nodeValue.trim() === '') continue;

					const convertedItem = convertNode(item);
					if (typeof obj[nodeName] === 'undefined') {
						obj[nodeName] = convertedItem;
					} else {
						if (!Array.isArray(obj[nodeName])) {
							obj[nodeName] = [obj[nodeName]];
						}
						obj[nodeName].push(convertedItem);
					}
				}
			}
		}
		// Usuń pusty obiekt @attributes, jeśli nie było atrybutów
		if (obj['@attributes'] && Object.keys(obj['@attributes']).length === 0) {
			delete obj['@attributes'];
		}
		// Dla pustych tagów np. <emptyTag/>, zwróć pusty string lub null, zamiast pustego obiektu
		if (
			node.nodeType === 1 &&
			Object.keys(obj).length === 0 &&
			node.childNodes.length === 0 &&
			(!node.attributes || node.attributes.length === 0)
		) {
			return ''; // lub null, w zależności od preferencji
		}
		return obj;
	};
	if (!root) return null; // Jeśli XML nie ma elementu root
	return { [root.nodeName]: convertNode(root) };
};

/**
 * Processes a fetched response, attempting to parse it based on the expected type.
 * If the expected type fails, it attempts to parse it as the other common type (JSON or XML).
 * @param {Response} response - The fetch Response object.
 * @param {'json'|'xml'} expectedType - The expected type of the content ('json' or 'xml').
 * @returns {Promise<{data: object, actualType: 'json'|'js_from_xml'}|null>}
 * An object containing the processed data (always a JS object if successful)
 * and the actual type it was derived from ('json' or 'js_from_xml').
 * Returns null if all parsing and conversion attempts fail.
 */
const _processFetchedData = async (response, expectedType) => {
	let parsedData = null;
	let sourceType = null;
	let responseClone;

	if (expectedType === 'json') {
		responseClone = response.clone();
		let result = await _tryParseJSON(responseClone);
		if (result) {
			parsedData = result.data;
			sourceType = result.type; // 'json'
			// console.log(
			// 	`%cData successfully parsed as JSON (expected JSON).`,
			// 	'color: blue; font-weight: bold;'
			// );
		} else {
			console.warn(
				`%cFailed to parse as JSON (expected JSON). Attempting to parse as XML.`,
				'color: orange;'
			);
			responseClone = response.clone();
			result = await _tryParseXML(responseClone);
			if (result) {
				parsedData = result.data; // XML Document
				sourceType = result.type; // 'xml'
				// console.log(
				// 	`%cData successfully parsed as XML (expected JSON, but was XML).`,
				// 	'color: blue; font-weight: bold;'
				// );
			} else {
				console.error(
					`%cFailed to parse as JSON and also failed as XML.`,
					'color: red; font-weight: bold;'
				);
			}
		}
	} else if (expectedType === 'xml') {
		responseClone = response.clone();
		let result = await _tryParseXML(responseClone);
		if (result) {
			parsedData = result.data; // XML Document
			sourceType = result.type; // 'xml'
			// console.log(
			// 	`%cData successfully parsed as XML (expected XML).`,
			// 	'color: blue; font-weight: bold;'
			// );
		} else {
			console.warn(
				`%cFailed to parse as XML (expected XML). Attempting to parse as JSON.`,
				'color: orange;'
			);
			responseClone = response.clone();
			result = await _tryParseJSON(responseClone);
			if (result) {
				parsedData = result.data;
				sourceType = result.type; // 'json'
				// console.log(
				// 	`%cData successfully parsed as JSON (expected XML, but was JSON).`,
				// 	'color: blue; font-weight: bold;'
				// );
			} else {
				console.error(
					`%cFailed to parse as XML and also failed as JSON.`,
					'color: red; font-weight: bold;'
				);
			}
		}
	} else {
		console.error(
			`%cUnknown expected type for processing: ${expectedType}`,
			'color: red; font-weight: bold;'
		);
		return null;
	}

	if (!parsedData) {
		try {
			const errorText = await response.text();
			console.error(
				`%cRaw response text on final processing failure: ${errorText.substring(
					0,
					500
				)}...`,
				'color: darkred;'
			);
		} catch (e) {
			console.error(
				'%cCould not get raw response text on final processing failure.',
				'color: darkred;'
			);
		}
		return null;
	}

	// Jeśli dane pochodzą z XML, przekonwertuj je teraz na obiekt JS
	if (sourceType === 'xml') {
		if (parsedData instanceof Document) {
			// console.log(
			// 	'%cConverting XML data to JS object...',
			// 	'color: purple; font-weight: bold;'
			// );
			try {
				const jsObjectFromXml = _xmlToJsObject(parsedData);
				if (jsObjectFromXml) {
					// console.log(
					// 	'%cSuccessfully converted XML to JS object:',
					// 	'color: purple;',
					// 	jsObjectFromXml
					// );
					// Zwracamy obiekt JS i oznaczamy, że pochodzi z XML
					return {
						data: jsObjectFromXml,
						actualType: 'js_from_xml',
					};
				} else {
					console.error(
						'%cXML to JS conversion returned null or invalid object.',
						'color: red;'
					);
					return null;
				}
			} catch (conversionError) {
				console.error(
					'%cError during XML to JS conversion:',
					'color: red;',
					conversionError
				);
				return null;
			}
		} else {
			console.error(
				'%cExpected XML Document for conversion, but received something else.',
				'color: red;',
				parsedData
			);
			return null;
		}
	} else if (sourceType === 'json') {
		return { data: parsedData, actualType: 'json' };
	}
	console.error(
		'%cReached unexpected state in processFetchedData.',
		'color: red;'
	);
	return null;
};

/**
 * Fetches and processes CRM data.
 * It tries to get the filename from a backend endpoint, then fetches and processes the file.
 * Handles fallbacks and API triggering.
 * @param {string} pageUrl - The current page URL.
 * @param {object} crmConfig - CRM integration configuration (projectSetup.website.crm_integration).
 * @param {object} envVars - Object containing environment variables like REACT_APP_BACKEND_API_RESULT_FILE etc.
 * @returns {Promise<object|null>} The processed JavaScript object with CRM data, or null if all attempts fail.
 */
export const fetchAndProcessCrmData = async (pageUrl, crmConfig, envVars) => {
	if (!crmConfig || !crmConfig.enabled) {
		console.log(
			'%cCRM integration is disabled in projectSetup.',
			'color: lightblue'
		);
		return null;
	}
	if (
		!envVars ||
		!envVars.REACT_APP_BACKEND_API_RESULT_FILE ||
		!envVars.REACT_APP_BACKEND_API_ENDPOINT ||
		!envVars.REACT_APP_BACKEND_API_RESULT_DIR
	) {
		console.error(
			'%cMissing required environment variables for API communication.',
			'color: red'
		);
		return null;
	}

	let processedApiData = null;
	let usedFallback = false;

	/**
	 * Attempts to fetch a local data file and process it based on the file name and extension.
	 * If the file is found, it processes the data and returns it.
	 * If the file is not found or processing fails, it logs a warning and returns null.
	 * @param {string} fileName - The name of the file to fetch (without extension).
	 * @param {string} extension - The file extension to try (e.g., 'json' or 'xml').
	 * @return {Promise<object|null>} The processed data as a JavaScript object, or null if the file was not found or processing failed.
	 */
	const _attemptToFetchAndProcess = async (fileName, extension) => {
		if (!fileName || !extension) {
			console.warn(
				'%cAttemptToFetchAndProcess: Invalid filename or extension provided.',
				'color: orange',
				{ fileName, extension }
			);
			return null;
		}
		const dataFileUrlNoExtension = `${pageUrl}${envVars.REACT_APP_BACKEND_API_RESULT_DIR}/${fileName}`;
		const localFileResult = await _tryFetchLocalDataFile(
			dataFileUrlNoExtension,
			[extension]
		);

		if (localFileResult.response.ok && localFileResult.type) {
			// console.log(
			// 	`%cLocal data file (${fileName}.${extension}) found. Processing...`,
			// 	'color: green; font-weight: bold;'
			// );

			const processed = await _processFetchedData(
				localFileResult.response,
				localFileResult.type
			);
			if (processed && processed.data) {
				// console.log(
				// 	`%cSuccessfully processed data from ${fileName}.${extension} as ${processed.actualType}.`,
				// 	'color: green;'
				// );
				return processed.data;
			} else {
				console.error(
					`%cFailed to process data from ${fileName}.${extension}. Content might be malformed.`,
					'color: red;'
				);
			}
		} else {
			console.log(
				`%cLocal data file ${fileName}.${extension} not found or error (Status: ${localFileResult.response.status}).`,
				'color: orange;'
			);
		}
		return null;
	};

	try {
		// 1. Zapytaj o nazwę pliku
		// console.log(
		// 	'%cFetching CRM data file name from backend...',
		// 	'color: blue;'
		// );
		const fileNameEndpoint = `${pageUrl}${envVars.REACT_APP_BACKEND_API_RESULT_FILE}`;
		let fileNameResponse;
		try {
			fileNameResponse = await fetch(fileNameEndpoint);
		} catch (fetchError) {
			console.error(
				`%cNetwork error fetching CRM data file name from ${fileNameEndpoint}:`,
				'color: red;',
				fetchError
			);
			fileNameResponse = { ok: false, status: 0, statusText: 'Network Error' };
		}

		if (fileNameResponse.ok) {
			const nameData = await fileNameResponse.json();
			if (nameData && nameData.filename) {
				const { name: dynamicFileName, extension: dynamicFileExtension } =
					_getFileNameAndExtension(nameData.filename);
				// console.log(
				// 	`%cBackend suggests filename: ${dynamicFileName}.${dynamicFileExtension}`,
				// 	'color: blue;'
				// );
				if (dynamicFileExtension === 'json' || dynamicFileExtension === 'xml') {
					processedApiData = await _attemptToFetchAndProcess(
						dynamicFileName,
						dynamicFileExtension
					);
				} else {
					console.warn(
						`%cBackend suggested an unsupported file extension: ${dynamicFileExtension}. Ignoring.`,
						'color: orange;'
					);
				}
			} else {
				console.warn(
					'%cFilename endpoint responded OK, but filename was missing or invalid in response.',
					'color: orange;',
					nameData
				);
			}
		} else {
			console.warn(
				`%cFailed to fetch CRM data file name from backend (Status: ${fileNameResponse.status}).`,
				'color: orange;'
			);
		}

		// 2. Trigger API
		if (!processedApiData) {
			// console.log('%cTriggering API to refresh CRM data...', 'color: blue;');
			const triggerEndpoint = `${pageUrl}${envVars.REACT_APP_BACKEND_API_ENDPOINT}`;
			let triggerResponse;
			try {
				triggerResponse = await fetch(triggerEndpoint);
			} catch (fetchError) {
				console.error(
					`%cNetwork error triggering API endpoint ${triggerEndpoint}:`,
					'color: red;',
					fetchError
				);
				triggerResponse = { ok: false, status: 0, statusText: 'Network Error' };
			}

			if (triggerResponse.ok) {
				// console.log(
				// 	'%cBackend API endpoint successfully triggered. Waiting for data refresh...',
				// 	'color: green; font-weight: bold;'
				// );
				await new Promise((resolve) =>
					setTimeout(resolve, crmConfig.api_fetch_delay || 2000)
				);

				// console.log(
				// 	'%cRe-fetching CRM data file name from backend after trigger...',
				// 	'color: blue;'
				// );
				let fileNameResponseAfterTrigger;
				try {
					fileNameResponseAfterTrigger = await fetch(fileNameEndpoint);
				} catch (fetchError) {
					console.error(
						`%cNetwork error re-fetching CRM data file name from ${fileNameEndpoint}:`,
						'color: red;',
						fetchError
					);
					fileNameResponseAfterTrigger = {
						ok: false,
						status: 0,
						statusText: 'Network Error',
					};
				}

				if (fileNameResponseAfterTrigger.ok) {
					const nameDataAfterTrigger =
						await fileNameResponseAfterTrigger.json();
					if (nameDataAfterTrigger && nameDataAfterTrigger.filename) {
						const { name: dynamicFileName, extension: dynamicFileExtension } =
							_getFileNameAndExtension(nameDataAfterTrigger.filename);
						// console.log(
						// 	`%cBackend suggests filename after trigger: ${dynamicFileName}.${dynamicFileExtension}`,
						// 	'color: blue;'
						// );
						if (
							dynamicFileExtension === 'json' ||
							dynamicFileExtension === 'xml'
						) {
							processedApiData = await _attemptToFetchAndProcess(
								dynamicFileName,
								dynamicFileExtension
							);
						} else {
							console.warn(
								`%cBackend suggested an unsupported file extension after trigger: ${dynamicFileExtension}. Ignoring.`,
								'color: orange;'
							);
						}
					} else {
						console.warn(
							'%cFilename endpoint (after trigger) responded OK, but filename was missing or invalid.',
							'color: orange;',
							nameDataAfterTrigger
						);
					}
				} else {
					console.warn(
						`%cFailed to re-fetch CRM data file name after trigger (Status: ${fileNameResponseAfterTrigger.status}).`,
						'color: orange;'
					);
				}
			} else {
				console.error(
					`%cError triggering backend API endpoint (Status: ${triggerResponse.statusText}).`,
					'color: red;'
				);
			}
		}

		// 3. Próba z domyślną nazwą pliku
		if (!processedApiData) {
			console.warn(
				'%cCould not obtain data using backend-suggested filename. Attempting fallback to default file name.',
				'color: orange; font-weight: bold;'
			);
			usedFallback = true;
			const defaultFileName = crmConfig.default_file_name || 'result';
			const defaultFileExtensions = crmConfig.default_file_types || [
				'json',
				'xml',
			];

			for (const ext of defaultFileExtensions) {
				// console.log(
				// 	`%cAttempting to fetch default file: ${defaultFileName}.${ext}`,
				// 	'color: orange;'
				// );
				processedApiData = await _attemptToFetchAndProcess(
					defaultFileName,
					ext
				);
				if (processedApiData) {
					// console.log(
					// 	`%cSuccessfully used fallback data from ${defaultFileName}.${ext}. Data might not be the latest.`,
					// 	'color: #FF8C00; font-weight: bold;'
					// );
					break;
				}
			}
		}

		if (processedApiData) {
			if (usedFallback) {
				console.warn(
					'%cCRM data was loaded using a fallback mechanism. It might not be the most current data.',
					'color: #FF8C00; font-weight: bold;'
				);
			}
			return processedApiData;
		} else {
			console.error(
				'%cFailed to load CRM data after all attempts (apiUtils).',
				'color: red; font-weight: bold;'
			);
			return null;
		}
	} catch (error) {
		console.error(
			'%cCritical error in fetchAndProcessCrmData (apiUtils):',
			'color: red; font-weight: bold;',
			error
		);
		return null;
	}
};
